import { useEffect, useState } from 'react';
import { useNotify } from '@hotjar/hotsauce';
import type { GTMAccount, GTMContainer } from '@hotjar/webapp/domains/activation/gtm/model';
import { createAndPublishTag } from '@hotjar/webapp/domains/activation/gtm/api';
import { useCurrentSite } from '@hotjar/site';
import { useModalTrackingCode } from '@hotjar/webapp/hooks/useModalTrackingCode';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { GTMSelect, GTMConfirm, GTMVerify, GTMLoader } from './steps';
import { useGTMIntegrationTracking } from './hooks/useGTMIntegrationTracking';
import { GTM_STEP } from './types';

interface IntegrateWithGTMProps {
  isInstalled: boolean;
  onBackClick: () => void;
}

export const IntegrateWithGTM = ({
  isInstalled,
  onBackClick,
}: IntegrateWithGTMProps): JSX.Element => {
  const { data: currentSite } = useCurrentSite();
  const [step, setStep] = useState<GTM_STEP>(isInstalled ? GTM_STEP.VERIFY : GTM_STEP.SELECT);
  const [account, setAccount] = useState<GTMAccount>();
  const [container, setContainer] = useState<GTMContainer>();
  const [isPublishing, setIsPublishing] = useState(false);
  const { trackIntegrationStep } = useGTMIntegrationTracking();
  const { show: showTrackingCodeModal } = useModalTrackingCode();
  const track = useTrack();

  const { showToast } = useNotify();
  const onGTMContainerSelect = (selectedAccount: GTMAccount, selectedContainer: GTMContainer) => {
    setStep(GTM_STEP.CONFIRM);
    setAccount(selectedAccount);
    setContainer(selectedContainer);
  };
  const onConfirm = () => {
    if (currentSite && account && container) {
      trackIntegrationStep('Confirmed Selection');
      setIsPublishing(true);
      createAndPublishTag(currentSite, account, container)
        .then(({ success }) => {
          if (!success) throw new Error('Failed to confirm');
          trackIntegrationStep('Published successfully');
          setStep(GTM_STEP.LOADING);
        })
        .catch(() => {
          setStep(GTM_STEP.SELECT);
          showToast({
            text: "There's a Hotjar tag present in the container already.",
            kind: 'danger',
          });
          trackIntegrationStep('Already has tag');
        })
        .finally(() => setIsPublishing(false));
    }
  };
  const onGTMCheckFinished = (success: boolean) => {
    trackIntegrationStep(success ? 'Check successfull' : 'Check failed');
    setStep(GTM_STEP.VERIFY);
  };
  const onVerifyClick = () => {
    track.click('gtm', 'Type of Installation', {});

    if (currentSite) {
      showTrackingCodeModal(currentSite, 2, 'Setup Hotjar Component');
    }
  };
  useEffect(() => {
    switch (step) {
      case GTM_STEP.SELECT:
        trackIntegrationStep('Select Containers');
        break;
      case GTM_STEP.CONFIRM:
        trackIntegrationStep('Confirm Selection');
        break;
      default:
        break;
    }
  }, [step, trackIntegrationStep]);

  switch (step) {
    case GTM_STEP.SELECT:
      return <GTMSelect onSubmit={onGTMContainerSelect} onBackClick={onBackClick} />;
    case GTM_STEP.LOADING:
      return <GTMLoader onCheckFinished={onGTMCheckFinished} />;
    case GTM_STEP.VERIFY:
      return <GTMVerify onClick={onVerifyClick} onCancel={onBackClick} />;
    default:
      return (
        <GTMConfirm
          onClick={onConfirm}
          isPublishing={isPublishing}
          onCancel={() => setStep(GTM_STEP.SELECT)}
          containerName={container?.name as string}
          accountName={account?.name as string}
        />
      );
  }
};
